import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";
import {
    IAbortInputWorkflowProcessRequestBody,
    ICancelInputWorkflowRequestBody,
    IGetFileUploadUrlResponse
} from "src/network/NetworkTypes";
import { RootStore } from "src/stores/RootStore";
import { INVOICE_ORIGIN, INPUTWORKFLOW_STATE, INPUTWORKFLOW_FILE_STATE, INVOICE_FILE_TYPE } from "./InvoiceEnums";
import { NetworkConfig } from "src/network/NetworkConfig";
import { getRoleKey } from "src/network/User";
import { IInputWorkflow, IInputWorkflowFile, IInputWorkflowUploadFile, IRealestate } from "./InvoiceTypes";
import {
    GET_INPUTWORKFLOWS,
    SUBSCRIBE_TO_INPUTWORKFLOW_FILES,
    SUBSCRIBE_TO_INPUTWORKFLOWS,
    UPDATE_INPUTWORKFLOW_FILE_STATE,
    UPDATE_REALESTATE_ON_INPUTWORKFLOW_FILE
} from "src/api/cred";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import { Subscription } from "zen-observable-ts";
import { GetInputWorkflows, GetInputWorkflows_inputworkflows } from "src/api/generated/GetInputWorkflows";
import {
    SubscribeToInputWorkflowFiles,
    SubscribeToInputWorkflowFiles_inputworkflowfiles
} from "src/api/generated/SubscribeToInputWorkflowFiles";
import {
    SubscribeToInputWorkflows,
    SubscribeToInputWorkflows_inputworkflows
} from "src/api/generated/SubscribeToInputWorkflows";
import {
    UpdateInputWorkflowFileState,
    UpdateInputWorkflowFileStateVariables
} from "src/api/generated/UpdateInputWorkflowFileState";
import { v4 as uuidv4 } from "uuid";
import { replaceUmlauts, replaceParenthesesWithSingleUnderscore } from "./Utils";
import i18next from "i18next";
import { MessageType } from "src/components/notifications/Notifier";
import {
    UpdateRealestateIdOnInputworkflowFile,
    UpdateRealestateIdOnInputworkflowFileVariables
} from "src/api/generated/UpdateRealestateIdOnInputworkflowFile";

export enum LOADING_STATE {
    INIT,
    LOADING,
    UPDATING_INPUTWORKFLOW_FILE_STATE,
    STARTING_KREDI_FLOW,
    DELETING_INPUT_WORKFLOW
}

export const ACCEPTED_DATA_TYPES = ["application/pdf"];

export const ACCEPTED_EXTENSIONS = [".pdf"];

type RealestateDataMap = {
    [inputWorkflowFileId: string]: IRealestateField; // index 0: visa1, index 1: visa2, index 2: visa3
};

interface IRealestateField {
    currentSearchQuery: string;
    selectedQuery: string;
    selectedRealestateName: string;
}

export class ImportInvoiceFilesStore {
    rootStore: RootStore;

    areFilesBeingImported: boolean = false;
    isDraggingOverDropZone: boolean = false;
    fileInputRef: HTMLInputElement | null = null;

    currentlyProcessingInputWorkflows: IInputWorkflow[] = [];
    originalInputWorkflowStructure: GetInputWorkflows_inputworkflows[] = [];

    loadingState: LOADING_STATE = LOADING_STATE.INIT;

    inputWorkflowSubscription?: Subscription;
    inputWorkflowFileSubscription?: Subscription;

    isWorkflowFileStateUpdating: Set<string> = new Set();
    displayedInputWorkflowDropdowns: Set<string> = new Set();

    isDeleteInputWorkflowModalDisplayed: boolean = false;
    inputWorkflowToDelete?: IInputWorkflow;

    realestateFields: RealestateDataMap = {}; // Every InputWorkflowFile with State = 10 can be assigned to a realestate. This datastructure is used to store the state of the searchqueries

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            areFilesBeingImported: observable,
            isDraggingOverDropZone: observable,
            fileInputRef: observable,
            currentlyProcessingInputWorkflows: observable,
            loadingState: observable,
            inputWorkflowSubscription: observable,
            inputWorkflowFileSubscription: observable,
            isWorkflowFileStateUpdating: observable,
            displayedInputWorkflowDropdowns: observable,
            isDeleteInputWorkflowModalDisplayed: observable,
            inputWorkflowToDelete: observable,
            realestateFields: observable,

            setAreFilesBeingImported: action,
            setIsDraggingOverDropZone: action,
            setFileInputRef: action,
            loadInputWorkflows: action,
            setLoadingState: action,
            setCurrentlyProcessingInputWorkflows: action,
            startInputWorkflowFileSubscription: action,
            stopInputWorkflowFileSubscription: action,
            onInputWorkflowFileUpdateReceived: action,
            startInputWorkflowSubscription: action,
            stopInputWorkflowSubscription: action,
            onInputWorkflowUpdateReceived: action,
            setInputWorkflowFileSubscription: action,
            setInputWorkflowSubscription: action,
            init: action,
            initiateRealestateFields: action,
            dispose: action,
            removeInputWorkflowAfterTimeout: action,
            updateInputWorkWorkflowFileState: action,
            toggleInputWorkflowFileDropdowns: action,
            closeAllInputWorkflowFileDropdowns: action,
            openAllInputWorkflowFileDropdowns: action,
            uploadInputWorkflowFileToBlobStorage: action,
            deleteInputWorkflowFile: action,
            deleteInputWorkflow: action,
            abortInputWorkflow: action,
            setIsDeleteInputWorkflowModalDisplayed: action,
            setInputWorkflowToDelete: action,
            setCurrentRealestateSearchQuery: action,
            setSelectedRealestateQuery: action,
            setSelectedRealestateName: action,
            resetRealestate: action,
            resetRealestateFields: action,

            workflowsReadyToCreateInvoices: computed,
            filteredInputWorkflows: computed
        });

        reaction(
            () => this.rootStore.invoiceListStore.invoiceFilter.query,
            (newQuery, oldQuery) => {
                // Check if the previous query was non-empty and the new one is empty.
                if (oldQuery && newQuery === "") {
                    this.closeAllInputWorkflowFileDropdowns();
                }
            }
        );
    }

    init = async () => {
        const realestateSearchStore = this.rootStore.realestateSearchStore;
        await realestateSearchStore.loadRealestateData();

        const loadedInputWorkflows = await this.loadInputWorkflows();

        this.initiateRealestateFields(loadedInputWorkflows);

        this.startInputWorkflowSubscription();
        this.startInputWorkflowFileSubscription();
    };

    initiateRealestateFields = (loadedInputWorkflows: IInputWorkflow[]) => {
        loadedInputWorkflows.forEach((inputWorkflow) => {
            inputWorkflow.inputWorkflowFiles?.forEach((inputWorkflowFile) => {
                runInAction(() => {
                    if (inputWorkflowFile.id && inputWorkflowFile.state === INPUTWORKFLOW_FILE_STATE.READY) {
                        if (inputWorkflowFile.realestateId && inputWorkflowFile.realestate !== undefined) {
                            const formattedRealestateName = inputWorkflowFile.realestate
                                ? this.getFormattedRealestateFieldValue(inputWorkflowFile.realestate)
                                : "";

                            this.realestateFields[inputWorkflowFile.id] = {
                                currentSearchQuery: formattedRealestateName,
                                selectedQuery: formattedRealestateName,
                                selectedRealestateName: formattedRealestateName
                            };
                        } else {
                            this.realestateFields[inputWorkflowFile.id] = {
                                currentSearchQuery: "",
                                selectedQuery: "",
                                selectedRealestateName: ""
                            };
                        }
                    }
                });
            });
        });
    };

    dispose = async () => {
        this.stopInputWorkflowSubscription();
        this.stopInputWorkflowFileSubscription();
    };

    setIsDeleteInputWorkflowModalDisplayed = (isDeleteInputWorkflowModalDisplayed: boolean) => {
        this.isDeleteInputWorkflowModalDisplayed = isDeleteInputWorkflowModalDisplayed;
    };

    setInputWorkflowToDelete = (inputWorkflow: IInputWorkflow | undefined) => {
        this.inputWorkflowToDelete = inputWorkflow;
    };

    setAreFilesBeingImported = (areFilesBeingImported: boolean) => {
        this.areFilesBeingImported = areFilesBeingImported;
    };

    setIsDraggingOverDropZone = (isDraggingOverDropZone: boolean) => {
        this.isDraggingOverDropZone = isDraggingOverDropZone;
    };

    setFileInputRef = (fileInputRef: HTMLInputElement | null) => {
        this.fileInputRef = fileInputRef;
    };

    setCurrentlyProcessingInputWorkflows = (currentlyProcessingInputWorkflows: IInputWorkflow[]) => {
        this.currentlyProcessingInputWorkflows = currentlyProcessingInputWorkflows;
    };

    setLoadingState = (loadingState: LOADING_STATE) => {
        this.loadingState = loadingState;
    };

    setInputWorkflowFileSubscription = (inputWorkflowFileSubscription: Subscription | undefined) => {
        this.inputWorkflowFileSubscription = inputWorkflowFileSubscription;
    };

    setInputWorkflowSubscription = (inputWorkflowSubscription: Subscription | undefined) => {
        this.inputWorkflowSubscription = inputWorkflowSubscription;
    };

    setCurrentRealestateSearchQuery(inputWorkflowFileId: string, query: string) {
        this.realestateFields[inputWorkflowFileId].currentSearchQuery = query;
    }

    setSelectedRealestateQuery(inputWorkflowFileId: string, query: string) {
        this.realestateFields[inputWorkflowFileId].selectedQuery = query;
    }

    setSelectedRealestateName(inputWorkflowFileId: string, query: string) {
        this.realestateFields[inputWorkflowFileId].selectedRealestateName = query;
    }

    getCurrentRealestateSearchQuery = (inputWorkflowFileId: string): string => {
        return this.realestateFields[inputWorkflowFileId]?.currentSearchQuery ?? "";
    };

    getSelectedRealestateQuery = (inputWorkflowFileId: string): string => {
        return this.realestateFields[inputWorkflowFileId]?.selectedQuery ?? "";
    };

    getSelectedRealestateName = (inputWorkflowFileId: string): string => {
        return this.realestateFields[inputWorkflowFileId]?.selectedRealestateName ?? "";
    };

    getFormattedRealestateFieldValue = (realestate: IRealestate): string => {
        return `${realestate.name} [${realestate.number}]`;
    };

    isRealestateSelected = (inputWorkflowFileId: string): boolean => {
        let isRealestateSelected = false;

        this.currentlyProcessingInputWorkflows.forEach((inputWorkflow) => {
            inputWorkflow.inputWorkflowFiles?.forEach((inputWorkflowFile) => {
                if (inputWorkflowFile.id === inputWorkflowFileId) {
                    isRealestateSelected = inputWorkflowFile.realestateId !== undefined;
                }
            });
        });

        return isRealestateSelected;
    };

    resetRealestate = (inputWorkflowFileId: string) => {
        this.currentlyProcessingInputWorkflows.forEach((inputWorkflow) => {
            inputWorkflow.inputWorkflowFiles?.forEach((inputWorkflowFile) => {
                if (inputWorkflowFile.id === inputWorkflowFileId) {
                    runInAction(() => {
                        inputWorkflowFile.realestateId = undefined;
                        inputWorkflowFile.realestate = undefined;
                    });
                }
            });
        });

        this.resetRealestateFields(inputWorkflowFileId);
        this.updateRealestateOnInputWorkWorkflowFile(inputWorkflowFileId, null);
    };

    resetRealestateFields = (inputWorkflowFileId: string) => {
        this.realestateFields[inputWorkflowFileId].currentSearchQuery = "";
        this.realestateFields[inputWorkflowFileId].selectedQuery = "";
        this.realestateFields[inputWorkflowFileId].selectedRealestateName = "";
    };

    updateDataWithSelectedRealestate = (selectedRealestate: IRealestate | undefined, inputWorkflowFileId: string) => {
        // update realestateinfo in the datastructure
        if (selectedRealestate !== undefined) {
            this.currentlyProcessingInputWorkflows.forEach((inputWorkflow) => {
                inputWorkflow.inputWorkflowFiles?.forEach((inputWorkflowFile) => {
                    if (inputWorkflowFile.id === inputWorkflowFileId) {
                        runInAction(() => {
                            inputWorkflowFile.realestateId = selectedRealestate.id;
                            inputWorkflowFile.realestate = selectedRealestate;
                        });
                    }
                });
            });

            this.updateRealestateOnInputWorkWorkflowFile(inputWorkflowFileId, selectedRealestate.id);
        } else if (selectedRealestate === undefined) {
            this.updateRealestateOnInputWorkWorkflowFile(inputWorkflowFileId, null);
        }

        // Update the field-values with the selected RealestateVisaUser
        if (selectedRealestate) {
            const formattedRealestateName = this.getFormattedRealestateFieldValue(selectedRealestate);

            this.setCurrentRealestateSearchQuery(inputWorkflowFileId, formattedRealestateName);
            this.setSelectedRealestateQuery(inputWorkflowFileId, formattedRealestateName);
            this.setSelectedRealestateName(inputWorkflowFileId, formattedRealestateName);
        } else {
            this.resetRealestateFields(inputWorkflowFileId);
        }
    };

    toggleInputWorkflowFileDropdowns = (inputWorkflowId: string) => {
        if (this.displayedInputWorkflowDropdowns.has(inputWorkflowId)) {
            this.displayedInputWorkflowDropdowns.delete(inputWorkflowId);
        } else {
            this.displayedInputWorkflowDropdowns.add(inputWorkflowId);
        }
    };

    closeAllInputWorkflowFileDropdowns = () => {
        this.displayedInputWorkflowDropdowns.clear();
    };

    openAllInputWorkflowFileDropdowns = () => {
        this.currentlyProcessingInputWorkflows.forEach((inputWorkflow) => {
            this.displayedInputWorkflowDropdowns.add(inputWorkflow.id);
        });
    };

    mapInputWorkflowFromDbToObject = (inputWorkflow: GetInputWorkflows_inputworkflows): IInputWorkflow => {
        // Flatten files with level information
        const flattenFiles = (files: any[], level: number = 0): IInputWorkflowFile[] => {
            return files.flatMap((file) => {
                const realestate = file.realestate;

                let zip: string | null | undefined;
                let city: string | null | undefined;

                if (realestate && realestate.houses.length > 0) {
                    zip = realestate.houses[0].zip;
                    city = realestate.houses[0].city;
                }

                const currentFile: IInputWorkflowFile = {
                    id: file.id,
                    fileId: file.file?.id,
                    name: file.file?.name ?? undefined,
                    state: file.state,
                    parentFileId: file.parentfileid,
                    inputWorkflowId: file.inputworkflowid,
                    realestateId: file.realestateid,
                    realestate: {
                        id: realestate ? realestate.id : "",
                        number: realestate ? realestate.number : "",
                        name: realestate ? realestate.name : "",
                        zip: zip ?? "",
                        city: city ?? ""
                    },
                    level: level // Add level information
                };

                // Recursively process children and increase the level
                const childFiles = flattenFiles(file.children ?? [], level + 1);

                // Combine the current file with its children
                return [currentFile, ...childFiles];
            });
        };

        const inputWorkflowFiles: IInputWorkflowFile[] = flattenFiles(inputWorkflow.inputworkflowfiles);

        if (inputWorkflowFiles && inputWorkflowFiles.length > 0) {
            // if the inputWorkflowFileId doesn't exist in the realestateFields, create a new entry for each inputworkflowfile
            inputWorkflowFiles.forEach((inputWorkflowFile) => {
                if (inputWorkflowFile.id && !this.realestateFields[inputWorkflowFile.id]) {
                    this.realestateFields[inputWorkflowFile.id] = {
                        currentSearchQuery: "",
                        selectedQuery: "",
                        selectedRealestateName: ""
                    };
                }
            });
        }

        const inputWorkflowObject: IInputWorkflow = {
            id: inputWorkflow.id,
            emailId: inputWorkflow.emailid,
            origin: inputWorkflow.origin,
            state: inputWorkflow.state,
            workflowInstance: inputWorkflow.workflowinstance,
            inputWorkflowFiles: inputWorkflowFiles
        };

        return inputWorkflowObject;
    };

    loadInputWorkflows = async (): Promise<IInputWorkflow[]> => {
        this.setLoadingState(LOADING_STATE.LOADING);

        const { data }: { data: GetInputWorkflows } = await apolloClientInstance.query<GetInputWorkflows>({
            query: GET_INPUTWORKFLOWS,
            fetchPolicy: "no-cache"
        });

        const currentlyProcessingInputWorkflows: IInputWorkflow[] = data.inputworkflows.map((inputWorkflow) => {
            return this.mapInputWorkflowFromDbToObject(inputWorkflow);
        });

        this.setCurrentlyProcessingInputWorkflows(currentlyProcessingInputWorkflows);

        this.setLoadingState(LOADING_STATE.INIT);

        return currentlyProcessingInputWorkflows;
    };

    startInputWorkflowFileSubscription() {
        const inputWorkflowFileSubscription = apolloClientInstance
            .subscribe<SubscribeToInputWorkflowFiles>({
                query: SUBSCRIBE_TO_INPUTWORKFLOW_FILES
            })
            .subscribe({
                next: ({ data }) => {
                    if (data?.inputworkflowfiles && data.inputworkflowfiles.length > 0) {
                        this.onInputWorkflowFileUpdateReceived(data.inputworkflowfiles);
                    }
                },
                error: (err) => {
                    console.error("SUBSCRIBE_TO_INPUTWORKFLOW_FILES subscription error:", err);
                    this.stopInputWorkflowFileSubscription();
                }
            });

        this.setInputWorkflowFileSubscription(inputWorkflowFileSubscription);
    }

    stopInputWorkflowFileSubscription() {
        if (this.inputWorkflowFileSubscription) {
            this.inputWorkflowFileSubscription.unsubscribe();
            this.setInputWorkflowFileSubscription(undefined);
        }
    }

    onInputWorkflowFileUpdateReceived = (inputWorkflowFiles: SubscribeToInputWorkflowFiles_inputworkflowfiles[]) => {
        // subscription should always return only one (the last updated) input workflow  file
        if (inputWorkflowFiles && inputWorkflowFiles.length > 0) {
            const updatedInputWorkflowFile = inputWorkflowFiles[0];

            this.currentlyProcessingInputWorkflows.forEach((inputWorkflow) => {
                if (inputWorkflow.id === updatedInputWorkflowFile.inputworkflowid) {
                    inputWorkflow.inputWorkflowFiles?.forEach((inputWorkflowFile) => {
                        if (inputWorkflowFile.id === updatedInputWorkflowFile.id) {
                            inputWorkflowFile.state = updatedInputWorkflowFile.state;
                        }
                    });

                    this.setCurrentlyProcessingInputWorkflows([...this.currentlyProcessingInputWorkflows]);
                }
            });

            if (this.isWorkflowFileStateUpdating.has(updatedInputWorkflowFile.id)) {
                this.isWorkflowFileStateUpdating.delete(updatedInputWorkflowFile.id);
            }

            if (!this.realestateFields[updatedInputWorkflowFile.id]) {
                this.realestateFields[updatedInputWorkflowFile.id] = {
                    currentSearchQuery: "",
                    selectedQuery: "",
                    selectedRealestateName: ""
                };
            }
        }
    };

    startInputWorkflowSubscription() {
        const inputWorkflowSubscription = apolloClientInstance
            .subscribe<SubscribeToInputWorkflows>({
                query: SUBSCRIBE_TO_INPUTWORKFLOWS
            })
            .subscribe({
                next: ({ data }) => {
                    if (data?.inputworkflows && data.inputworkflows.length > 0) {
                        this.onInputWorkflowUpdateReceived(data.inputworkflows);
                    }
                },
                error: (err) => {
                    console.error("SUBSCRIBE_TO_INPUTWORKFLOW_FILES subscription error:", err);
                    this.stopInputWorkflowFileSubscription();
                }
            });

        this.setInputWorkflowFileSubscription(inputWorkflowSubscription);
    }

    stopInputWorkflowSubscription() {
        if (this.inputWorkflowSubscription) {
            this.inputWorkflowSubscription.unsubscribe();
            this.setInputWorkflowSubscription(undefined);
        }
    }

    onInputWorkflowUpdateReceived = (inputWorkflow: SubscribeToInputWorkflows_inputworkflows[]) => {
        // subscription should always return only one (the last updated) input workflow
        if (inputWorkflow && inputWorkflow.length > 0) {
            const updatedInputWorkflow = inputWorkflow[0];

            const isNewWorkflow = !this.currentlyProcessingInputWorkflows.some(
                (inputWorkflow) => inputWorkflow.id === updatedInputWorkflow.id
            );

            if (
                isNewWorkflow &&
                updatedInputWorkflow.state !== INPUTWORKFLOW_STATE.FINISHED &&
                updatedInputWorkflow.state !== INPUTWORKFLOW_STATE.CANCELLED
            ) {
                const newWorkflow = this.mapInputWorkflowFromDbToObject(updatedInputWorkflow);

                this.setCurrentlyProcessingInputWorkflows([newWorkflow, ...this.currentlyProcessingInputWorkflows]);
            } else {
                const updatedWorkflow = this.mapInputWorkflowFromDbToObject(updatedInputWorkflow);

                // replace the current workflow with the updated one
                const updatedWorkflowArray = this.currentlyProcessingInputWorkflows.map((workflow) =>
                    workflow.id === updatedWorkflow.id ? updatedWorkflow : workflow
                );

                this.setCurrentlyProcessingInputWorkflows([...updatedWorkflowArray]);

                if (updatedInputWorkflow.state === INPUTWORKFLOW_STATE.FINISHED) {
                    this.removeInputWorkflowAfterTimeout(updatedInputWorkflow);
                }
            }
        }
    };

    removeInputWorkflowAfterTimeout = (inputWorkflowToRemove: IInputWorkflow) => {
        setTimeout(() => {
            const newStagingFilesArray = this.currentlyProcessingInputWorkflows.filter(
                (inputWorkflow) => inputWorkflow.id !== inputWorkflowToRemove.id
            );
            this.setCurrentlyProcessingInputWorkflows([...newStagingFilesArray]);
        }, 10000);
    };

    canKrediFlowBeStarted = (inputWorkflow: IInputWorkflow): boolean => {
        const canKrediFlowBeStarted = inputWorkflow.inputWorkflowFiles
            ? inputWorkflow.inputWorkflowFiles.every(
                  (inputWorkflowFile) =>
                      inputWorkflowFile.state && inputWorkflowFile.state >= INPUTWORKFLOW_FILE_STATE.READY
              )
            : false;

        return canKrediFlowBeStarted;
    };

    updateRealestateOnInputWorkWorkflowFile = async (
        inputworkflowFileId: string,
        realestateId: string | null
    ): Promise<boolean> => {
        let successfullyUpdated = false;

        try {
            const { data } = await apolloClientInstance.mutate<
                UpdateRealestateIdOnInputworkflowFile,
                UpdateRealestateIdOnInputworkflowFileVariables
            >({
                mutation: UPDATE_REALESTATE_ON_INPUTWORKFLOW_FILE,
                variables: {
                    inputWorkflowFileId: inputworkflowFileId,
                    realestateId: realestateId
                }
            });

            if (data && data.update_file_inputworkflowfiles && data.update_file_inputworkflowfiles.affected_rows > 0) {
                successfullyUpdated = true;
            }
        } catch (error) {
            console.error("Error updating realestate on inputworkflow file: ", error);
        }

        return successfullyUpdated;
    };

    updateInputWorkWorkflowFileState = async (
        inputworkflowFileId: string,
        newState: INPUTWORKFLOW_FILE_STATE
    ): Promise<boolean> => {
        this.isWorkflowFileStateUpdating.add(inputworkflowFileId);

        let successfullyUpdated = false;

        try {
            const { data } = await apolloClientInstance.mutate<
                UpdateInputWorkflowFileState,
                UpdateInputWorkflowFileStateVariables
            >({
                mutation: UPDATE_INPUTWORKFLOW_FILE_STATE,
                variables: {
                    inputworkflowfileid: inputworkflowFileId,
                    state: newState
                }
            });

            if (data && data.updated_inputworkflowfiles) {
                successfullyUpdated = true;
            }
        } catch (error) {
            console.error("Error updating inputworkflow file state: ", error);
        }

        return successfullyUpdated;
    };

    abortInputWorkflow = async (inputWorkflow: IInputWorkflow): Promise<boolean> => {
        let wasWorkflowSuccessfullyAborted = false;

        const abortInputWorkflowProcessRequestBody: IAbortInputWorkflowProcessRequestBody = {
            inputworkflowid: inputWorkflow.id
        };

        try {
            const accessToken = this.rootStore.authStore.token;
            const tokenType = this.rootStore.authStore.tokenType;
            const role = this.rootStore.authStore.user?.role;

            const abortInputWorkflowProcessResponse = await fetch(NetworkConfig.abortInputWorkflowProcessUrl, {
                method: "POST",
                body: JSON.stringify(abortInputWorkflowProcessRequestBody),
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `${tokenType} ${accessToken}`,
                    "x-hasura-role": getRoleKey(role)
                }
            });

            if (abortInputWorkflowProcessResponse.status === 200) {
                wasWorkflowSuccessfullyAborted = true;
            } else {
                console.error(
                    "Error occured while trying create a new input workflow: ",
                    abortInputWorkflowProcessResponse
                );
            }
        } catch (error) {
            console.error("Error occured while trying create a new input workflow: ", error);
        }

        return wasWorkflowSuccessfullyAborted;
    };

    deleteInputWorkflow = async (inputWorkflow: IInputWorkflow): Promise<boolean> => {
        this.setLoadingState(LOADING_STATE.DELETING_INPUT_WORKFLOW);

        const inputWorkflowSuccessfullyAborted = await this.abortInputWorkflow(inputWorkflow);

        if (inputWorkflowSuccessfullyAborted) {
            const updatedInputWorkflows = this.currentlyProcessingInputWorkflows.filter(
                (workflow) => workflow.id !== inputWorkflow.id
            );

            this.setCurrentlyProcessingInputWorkflows(updatedInputWorkflows);
        }

        this.setIsDeleteInputWorkflowModalDisplayed(false);
        this.setLoadingState(LOADING_STATE.INIT);
        return inputWorkflowSuccessfullyAborted;
    };

    deleteInputWorkflowFile = async (inputWorkflowFileId: string): Promise<boolean> => {
        const stateSuccessfullyUpdated = await this.updateInputWorkWorkflowFileState(
            inputWorkflowFileId,
            INPUTWORKFLOW_FILE_STATE.DELETED
        );

        if (stateSuccessfullyUpdated) {
            this.currentlyProcessingInputWorkflows.forEach((inputWorkflow) => {
                const updatedInputWorkflowFiles = inputWorkflow.inputWorkflowFiles?.filter(
                    (inputWorkflowFile) => inputWorkflowFile.id !== inputWorkflowFileId
                );
                inputWorkflow.inputWorkflowFiles = updatedInputWorkflowFiles;
            });
        }

        return stateSuccessfullyUpdated;
    };

    uploadInputWorkflowFileToBlobStorage = async (
        inputWorkflowUploadFile: IInputWorkflowUploadFile
    ): Promise<
        | {
              inputWorkflowId: string;
              fileId: string;
          }
        | undefined
    > => {
        let successfullyUploadedFile = undefined;

        const newInputWorkflowId = uuidv4();
        const newFileId = uuidv4();

        try {
            const sasUrl = await this.getFileUploadUrl(newFileId, inputWorkflowUploadFile.extension ?? "pdf");

            if (!sasUrl) {
                console.error("Unable to get SAS URL for file upload.");
            }

            if (!inputWorkflowUploadFile) {
                console.error("No file provided for upload.");
            }

            const chunkSize = 4 * 1024 * 1024; // 4 MB
            const totalChunks = Math.ceil(inputWorkflowUploadFile.size / chunkSize);
            const uploadPromises = [];

            let cleanedUpFileName = replaceParenthesesWithSingleUnderscore(inputWorkflowUploadFile.name ?? "");
            cleanedUpFileName = replaceUmlauts(cleanedUpFileName);

            const tags = {
                name: cleanedUpFileName,
                type: INVOICE_FILE_TYPE.INPUTWORKFLOW_FILE.toString(),
                customerid: this.rootStore.authStore.user?.customerid ?? "",
                extension: inputWorkflowUploadFile.extension ?? "",
                mimetype: inputWorkflowUploadFile.mimetype ?? "",
                filedate: inputWorkflowUploadFile.fileDate ?? "",
                inputworkflowid: newInputWorkflowId
            };

            const encodedTags = Object.entries(tags)
                .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
                .join("&");

            const uploadChunk = async (start: number, end: number, chunkIndex: number): Promise<void> => {
                const blockId = Buffer.from(String(chunkIndex).padStart(5, "0")).toString("base64");
                const blobUrl = `${sasUrl}&comp=block&blockid=${blockId}`;

                const chunk = inputWorkflowUploadFile.fileObject.slice(start, end);

                const response = await fetch(blobUrl, {
                    method: "PUT",
                    headers: {
                        "x-ms-blob-type": "BlockBlob"
                    },
                    body: chunk
                });

                if (!response.ok) {
                    throw new Error(`Chunk upload failed with status: ${response.status}`);
                }
            };

            // Create all upload promises
            for (let i = 0; i < totalChunks; i++) {
                const start = i * chunkSize;
                const end = Math.min(start + chunkSize, inputWorkflowUploadFile.size);
                uploadPromises.push(uploadChunk(start, end, i));
            }

            // Wait for all chunks to finish uploading
            await Promise.all(uploadPromises);

            // Commit the block list
            const blockList = Array.from({ length: totalChunks }, (_, i) =>
                Buffer.from(String(i).padStart(5, "0")).toString("base64")
            );

            const blockListXml = `<?xml version="1.0" encoding="utf-8"?><BlockList>${blockList
                .map((id) => `<Uncommitted>${id}</Uncommitted>`)
                .join("")}</BlockList>`;

            const commitUrl = `${sasUrl}&comp=blocklist`;
            const commitResponse = await fetch(commitUrl, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/xml",
                    "x-ms-tags": encodedTags
                },
                body: blockListXml
            });

            if (commitResponse.ok) {
                successfullyUploadedFile = {
                    inputWorkflowId: newInputWorkflowId,
                    fileId: newFileId
                };

                const tempInputWorkflow: IInputWorkflow = {
                    id: newInputWorkflowId,
                    emailId: "",
                    origin: INVOICE_ORIGIN.MANUAL,
                    state: INPUTWORKFLOW_STATE.UI,
                    workflowInstance: "",
                    uiName: cleanedUpFileName
                };

                this.setCurrentlyProcessingInputWorkflows([
                    tempInputWorkflow,
                    ...this.currentlyProcessingInputWorkflows
                ]);
            } else {
                console.error("Failed to commit block list: ", commitResponse.status, commitResponse.statusText);
            }
        } catch (error) {
            console.error("Error occurred during file upload: ", error);
        }

        return successfullyUploadedFile;
    };

    getFileUploadUrl = async (fileId: string, extension: string): Promise<any | undefined> => {
        let fileUploadUrl = undefined;

        try {
            const accessToken = this.rootStore.authStore.token;
            const tokenType = this.rootStore.authStore.tokenType;
            const role = this.rootStore.authStore.user?.role;

            const getFileUploadUrlResponse = await fetch(
                `${NetworkConfig.getFileUploadUrl}?blobname=${fileId}.${extension}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `${tokenType} ${accessToken}`,
                        "x-hasura-role": getRoleKey(role)
                    }
                }
            );

            if (getFileUploadUrlResponse.status === 200) {
                const fileUploadUrlResponse: IGetFileUploadUrlResponse = await getFileUploadUrlResponse.json();
                fileUploadUrl = fileUploadUrlResponse.data.sasuri;
            } else {
                console.error("Error occured while trying to get file upload url: ", getFileUploadUrlResponse);
            }
        } catch (error) {
            console.error("Error occured while trying to get file upload url: ", error);
        }

        return fileUploadUrl;
    };

    startKrediFlow = async (inputWorkflow: IInputWorkflow): Promise<boolean> => {
        this.setLoadingState(LOADING_STATE.STARTING_KREDI_FLOW);

        // set the state of the krediflow to 7 (creating invoices)
        const updatedInputWorkflow = { ...inputWorkflow, state: INPUTWORKFLOW_STATE.CREATING_INVOICES };
        const updatedInputWorkflows = this.currentlyProcessingInputWorkflows.map((workflow) =>
            workflow.id === inputWorkflow.id ? updatedInputWorkflow : workflow
        );
        this.setCurrentlyProcessingInputWorkflows(updatedInputWorkflows);

        let wasKrediFlowSuccessfullyStarted = false;

        const cancelInputWorkflowVariables: ICancelInputWorkflowRequestBody = {
            inputworkflowid: inputWorkflow.id,
            cancelled: false
        };

        try {
            const accessToken = this.rootStore.authStore.token;
            const tokenType = this.rootStore.authStore.tokenType;
            const role = this.rootStore.authStore.user?.role;

            const createInputWorkflowResponse = await fetch(NetworkConfig.cancelInputWorkflowUrl, {
                method: "POST",
                body: JSON.stringify(cancelInputWorkflowVariables),
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `${tokenType} ${accessToken}`,
                    "x-hasura-role": getRoleKey(role)
                }
            });

            if (createInputWorkflowResponse.status === 200) {
                wasKrediFlowSuccessfullyStarted = true;
            } else {
                console.error(
                    "Error occured while trying to start the kredi flow process: ",
                    createInputWorkflowResponse
                );
            }
        } catch (error) {
            console.error("Error occured while trying to start the kredi flow process: ", error);
        }

        if (wasKrediFlowSuccessfullyStarted) {
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.kredi_flow.document_import.kredi_flow_successfully_started"),
                MessageType.SUCCESS
            );
        } else {
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.kredi_flow.document_import.kredi_flow_start_failed"),
                MessageType.ERROR
            );
        }

        this.setLoadingState(LOADING_STATE.STARTING_KREDI_FLOW);
        return wasKrediFlowSuccessfullyStarted;
    };

    getInputWorkflowName = (inputWorkflow: IInputWorkflow): string => {
        return inputWorkflow.inputWorkflowFiles && inputWorkflow.inputWorkflowFiles.length > 0
            ? inputWorkflow.inputWorkflowFiles[0].name ?? ""
            : inputWorkflow.state === INPUTWORKFLOW_STATE.UI
            ? inputWorkflow.uiName ?? ""
            : i18next.t("screens.kredi_flow.document_import.list.new_input_workflow");
    };

    get workflowsReadyToCreateInvoices(): IInputWorkflow[] {
        return this.currentlyProcessingInputWorkflows.filter((workflow) => this.canKrediFlowBeStarted(workflow));
    }

    get filteredInputWorkflows(): IInputWorkflow[] {
        const currentQuery = this.rootStore.invoiceListStore.invoiceFilter.query;

        if (currentQuery === "") {
            return this.currentlyProcessingInputWorkflows;
        }

        return this.currentlyProcessingInputWorkflows.filter((inputWorkflow) => {
            const inputWorkflowName = this.getInputWorkflowName(inputWorkflow);

            const queryMatchesWorkflowName = this.doesQueryMatchWorkflowName(inputWorkflowName, currentQuery);
            const queryMatchesWorkflowFileName = this.doesQueryMatchWorkflowFileName(inputWorkflow, currentQuery);
            const queryMatchesWorkflowState = this.doesQueryMatchWorkflowState(inputWorkflow, currentQuery);
            const queryMatchesWorkflowFileState = this.doesQueryMatchWorkflowFileState(inputWorkflow, currentQuery);

            const resultOnlyFoundInWorkflowFileName = !queryMatchesWorkflowName && queryMatchesWorkflowFileName;

            this.handleDropdownToggle(inputWorkflow, resultOnlyFoundInWorkflowFileName, queryMatchesWorkflowFileState);

            return (
                queryMatchesWorkflowName ||
                queryMatchesWorkflowFileName ||
                queryMatchesWorkflowState ||
                queryMatchesWorkflowFileState
            );
        });
    }

    filteredRealestateData(inputWorkflowFileId: string): IRealestate[] {
        const realestateData = this.rootStore.realestateSearchStore.realestateData;

        let currentRealestateSearchQuery = this.getCurrentRealestateSearchQuery(inputWorkflowFileId);
        let selectedRealestateQuery = this.getSelectedRealestateQuery(inputWorkflowFileId);

        if (currentRealestateSearchQuery === selectedRealestateQuery) {
            return realestateData;
        }

        if (currentRealestateSearchQuery === "") {
            return realestateData;
        }

        const filteredRealestateData = realestateData.filter((realestate) => {
            const realestateName = `${realestate.nameZipCity} [${realestate.number}]`;

            return realestateName.toLowerCase().includes(currentRealestateSearchQuery.toLowerCase());
        });

        return filteredRealestateData;
    }

    private doesQueryMatchWorkflowName(inputWorkflowName: string | undefined, currentQuery: string): boolean {
        return inputWorkflowName?.toLowerCase().includes(currentQuery.toLowerCase()) ?? false;
    }

    private doesQueryMatchWorkflowFileName(inputWorkflow: IInputWorkflow, currentQuery: string): boolean {
        return (
            inputWorkflow.inputWorkflowFiles?.some((inputWorkflowFile) =>
                inputWorkflowFile.name?.toLowerCase().includes(currentQuery.toLowerCase())
            ) ?? false
        );
    }

    private doesQueryMatchWorkflowState(inputWorkflow: IInputWorkflow, currentQuery: string): boolean {
        return i18next
            .t(`screens.kredi_flow.document_import.input_workflow.state.${inputWorkflow.state}`)
            .toLowerCase()
            .includes(currentQuery.toLowerCase());
    }

    private doesQueryMatchWorkflowFileState(inputWorkflow: IInputWorkflow, currentQuery: string): boolean {
        return (
            inputWorkflow.inputWorkflowFiles?.some((inputWorkflowFile) =>
                i18next
                    .t(`screens.kredi_flow.document_import.input_workflow_file.state.${inputWorkflowFile.state}`)
                    .toLowerCase()
                    .includes(currentQuery.toLowerCase())
            ) ?? false
        );
    }

    private handleDropdownToggle(
        inputWorkflow: IInputWorkflow,
        resultOnlyFoundInWorkflowFileName: boolean,
        searchQueryContainedInWorkflowFileState: boolean
    ): void {
        if (resultOnlyFoundInWorkflowFileName || searchQueryContainedInWorkflowFileState) {
            if (!this.displayedInputWorkflowDropdowns.has(inputWorkflow.id)) {
                this.toggleInputWorkflowFileDropdowns(inputWorkflow.id);
            }
        }
    }
}
